import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useLanguage } from '../translations/LanguageContext';
import {
  Search, ChevronDown, ChevronLeft, ChevronRight, X, Image as ImageIcon,
  Loader2, SlidersHorizontal, TrendingUp, Camera,
} from 'lucide-react';

const TYPE_COLORS = { exact: 'var(--green)', manual: 'var(--purple)', recognized: 'var(--cyan)' };

const resultBadge = (result) => {
  if (!result) return null;
  const r = result.toLowerCase();
  let bg = 'rgba(139,92,246,0.15)';
  let color = 'var(--purple)';
  if (r.includes('sold') && !r.includes('not')) { bg = 'rgba(52,211,153,0.15)'; color = 'var(--green)'; }
  else if (r.includes('negotiate') || r.includes('negotiat')) { bg = 'rgba(250,204,21,0.15)'; color = 'var(--yellow)'; }
  else if (r.includes('not sold') || r.includes('unsold')) { bg = 'rgba(248,113,113,0.15)'; color = 'var(--red)'; }
  return { bg, color };
};

const fmtJPY = (v) => {
  const n = Number(v);
  if (!n && n !== 0) return '--';
  return `\u00a5${n.toLocaleString()}`;
};

function MarketPrices({ user }) {
  const { t } = useLanguage();

  // Check if chassis was passed from Dashboard quick search
  const initialChassis = (() => {
    const val = sessionStorage.getItem('mp_chassis');
    if (val) { sessionStorage.removeItem('mp_chassis'); return val; }
    return '';
  })();

  // --- state ---
  const [view, setView] = useState('search'); // search | results | detail
  const [chassis, setChassis] = useState(initialChassis);
  const [endDate, setEndDate] = useState('');
  const [yearFilter, setYearFilter] = useState('');
  const [colorFilter, setColorFilter] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [results, setResults] = useState([]);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [lightbox, setLightbox] = useState(null); // { images, index }

  // Auto-search if chassis was passed from Dashboard
  const autoSearched = useRef(false);
  useEffect(() => {
    if (initialChassis && !autoSearched.current) {
      autoSearched.current = true;
      // Trigger search after render
      setTimeout(() => doSearch(), 0);
    }
  }, []);

  // --- search ---
  const doSearch = useCallback(async () => {
    if (!chassis.trim()) return;
    setLoading(true);
    setError('');
    setResults([]);
    try {
      const params = new URLSearchParams({ chassis_number: chassis.trim() });
      if (endDate) params.append('end_date', endDate);
      if (yearFilter) params.append('year', yearFilter);
      if (colorFilter) params.append('colors[]', colorFilter);
      const res = await fetch(`/api/aleado-search?${params.toString()}`);
      const json = await res.json();
      if (!res.ok) throw new Error(json.error?.message || json.error || json.message || `API error (${res.status})`);
      if (json.error && json.error.message) throw new Error(json.error.message);
      const items = (json.data || []).map(item => ({
        ...item,
        ...(item.details || {}),
      }));
      setResults(items);
      setView('results');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [chassis, endDate, yearFilter, colorFilter]);

  // --- fetch detail ---
  const fetchDetail = useCallback(async (id) => {
    setDetailLoading(true);
    setError('');
    try {
      const res = await fetch(`/api/aleado-detail?id=${encodeURIComponent(id)}`);
      const json = await res.json();
      if (!res.ok) throw new Error(json.error?.message || json.error || json.message || `API error (${res.status})`);
      const lot = json.data?.[0] || json.data || {};
      setDetail(lot);
      setView('detail');
    } catch (err) {
      setError(err.message);
    } finally {
      setDetailLoading(false);
    }
  }, []);

  // --- styles ---
  const card = {
    background: 'var(--bg2)',
    border: '1px solid var(--border)',
    borderRadius: 12,
    padding: 16,
    cursor: 'pointer',
    transition: 'border-color 0.15s',
  };
  const badge = (bg, color) => ({
    display: 'inline-block',
    padding: '2px 8px',
    borderRadius: 999,
    fontSize: 11,
    fontWeight: 600,
    background: bg,
    color,
  });
  const inputStyle = {
    background: 'var(--bg3)',
    border: '1px solid var(--border)',
    borderRadius: 8,
    padding: '10px 14px',
    color: 'var(--text1)',
    fontSize: 14,
    outline: 'none',
    width: '100%',
  };
  const btnPrimary = {
    background: 'var(--blue)',
    color: '#fff',
    border: 'none',
    borderRadius: 8,
    padding: '10px 20px',
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  };

  // ===================== SEARCH VIEW =====================
  if (view === 'search' || (view === 'results' && results.length === 0 && !loading)) {
    return (
      <div style={{ maxWidth: 640, margin: '0 auto', padding: '40px 16px' }}>
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <TrendingUp size={40} strokeWidth={1.5} style={{ color: 'var(--blue)', marginBottom: 12 }} />
          <h1 style={{ color: 'var(--text1)', fontSize: 24, fontWeight: 700, margin: '0 0 8px' }}>
            Market Prices
          </h1>
          <p style={{ color: 'var(--text3)', fontSize: 14, margin: 0 }}>
            Search auction results by chassis number
          </p>
        </div>

        {/* Search input */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
          <div style={{ flex: 1, position: 'relative' }}>
            <input
              style={{ ...inputStyle, paddingRight: 40, fontSize: 16 }}
              placeholder="Enter chassis number"
              value={chassis}
              onChange={e => setChassis(e.target.value.toUpperCase())}
              onKeyDown={e => e.key === 'Enter' && doSearch()}
            />
            {chassis && (
              <button
                onClick={() => setChassis('')}
                style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: 'var(--text3)', cursor: 'pointer', padding: 4 }}
              >
                <X size={16} />
              </button>
            )}
          </div>
          <button style={btnPrimary} onClick={doSearch} disabled={loading || !chassis.trim()}>
            {loading ? <Loader2 size={18} className="spin" /> : <Search size={18} />}
            {t('btn_search') || 'Search'}
          </button>
        </div>

        {/* Optional filters toggle */}
        <button
          onClick={() => setShowFilters(!showFilters)}
          style={{ background: 'none', border: 'none', color: 'var(--text3)', fontSize: 13, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}
        >
          <SlidersHorizontal size={14} />
          Filters
          <ChevronDown size={14} style={{ transform: showFilters ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
        </button>
        {showFilters && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, marginBottom: 16, background: 'var(--bg2)', padding: 12, borderRadius: 8, border: '1px solid var(--border)' }}>
            <div>
              <label style={{ fontSize: 11, color: 'var(--text3)', display: 'block', marginBottom: 4 }}>End Date</label>
              <input type="date" style={inputStyle} value={endDate} onChange={e => setEndDate(e.target.value)} />
            </div>
            <div>
              <label style={{ fontSize: 11, color: 'var(--text3)', display: 'block', marginBottom: 4 }}>Year</label>
              <input style={inputStyle} placeholder="e.g. 2020" value={yearFilter} onChange={e => setYearFilter(e.target.value)} />
            </div>
            <div>
              <label style={{ fontSize: 11, color: 'var(--text3)', display: 'block', marginBottom: 4 }}>Color</label>
              <input style={inputStyle} placeholder="e.g. White" value={colorFilter} onChange={e => setColorFilter(e.target.value)} />
            </div>
          </div>
        )}

        {error && (
          <div style={{ background: 'rgba(248,113,113,0.1)', border: '1px solid var(--red)', borderRadius: 8, padding: 12, color: 'var(--red)', fontSize: 13, marginTop: 12 }}>
            {error}
          </div>
        )}

        {view === 'results' && results.length === 0 && !loading && !error && (
          <div style={{ textAlign: 'center', color: 'var(--text3)', marginTop: 40, fontSize: 14 }}>
            No auction results found for this chassis number.
          </div>
        )}
      </div>
    );
  }

  // ===================== RESULTS VIEW =====================
  if (view === 'results') {
    return (
      <div style={{ padding: '16px' }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
          <button
            onClick={() => setView('search')}
            style={{ background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 8, padding: '6px 12px', color: 'var(--text2)', fontSize: 13, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}
          >
            <ChevronLeft size={16} /> {t('btn_back') || 'Back'}
          </button>
          <h2 style={{ color: 'var(--text1)', fontSize: 18, fontWeight: 700, margin: 0 }}>
            Results: {chassis}
          </h2>
          <span style={{ color: 'var(--text3)', fontSize: 13 }}>{results.length} {results.length === 1 ? 'lot' : 'lots'}</span>
        </div>

        {detailLoading && (
          <div style={{ textAlign: 'center', padding: 20, color: 'var(--text3)' }}>
            <Loader2 size={24} className="spin" style={{ marginBottom: 8 }} />
            <div style={{ fontSize: 13 }}>Loading lot details...</div>
          </div>
        )}

        {error && (
          <div style={{ background: 'rgba(248,113,113,0.1)', border: '1px solid var(--red)', borderRadius: 8, padding: 12, color: 'var(--red)', fontSize: 13, marginBottom: 12 }}>
            {error}
          </div>
        )}

        {/* Grid */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 12 }}>
          {results.map((item, i) => {
            const rb = resultBadge(item.result);
            return (
              <div
                key={item.id || i}
                style={card}
                onClick={() => fetchDetail(item.id)}
                onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--blue)'; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; }}
              >
                {/* Image + badges */}
                <div style={{ position: 'relative', marginBottom: 10 }}>
                  {item.chassis_image ? (
                    <img
                      src={item.chassis_image}
                      alt={`${item.maker} ${item.model}`}
                      style={{ width: '100%', height: 160, objectFit: 'cover', borderRadius: 8, background: 'var(--bg3)' }}
                      loading="lazy"
                    />
                  ) : (
                    <div style={{ width: '100%', height: 160, borderRadius: 8, background: 'var(--bg3)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Camera size={32} style={{ color: 'var(--text3)' }} />
                    </div>
                  )}
                  {/* Type badge */}
                  <span style={{ position: 'absolute', top: 8, left: 8, ...badge(TYPE_COLORS[item.type] ? `${TYPE_COLORS[item.type]}22` : 'rgba(139,92,246,0.15)', TYPE_COLORS[item.type] || 'var(--purple)') }}>
                    {item.type || 'unknown'}
                  </span>
                  {/* Images count */}
                  {item.images_amount > 0 && (
                    <span style={{ position: 'absolute', top: 8, right: 8, ...badge('rgba(0,0,0,0.6)', '#fff'), display: 'flex', alignItems: 'center', gap: 4 }}>
                      <ImageIcon size={12} /> {item.images_amount}
                    </span>
                  )}
                </div>

                {/* Info */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 6 }}>
                  <div>
                    <div style={{ color: 'var(--text1)', fontWeight: 700, fontSize: 15 }}>
                      {item.maker} {item.model}
                    </div>
                    <div style={{ color: 'var(--text3)', fontSize: 12 }}>
                      {item.frame}
                    </div>
                  </div>
                  {rb && (
                    <span style={badge(rb.bg, rb.color)}>{item.result}</span>
                  )}
                </div>

                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
                  {item.year && <span style={badge('var(--bg3)', 'var(--text2)')}>{item.year}</span>}
                  {item.displacement && <span style={badge('var(--bg3)', 'var(--text2)')}>{item.displacement}cc</span>}
                  {item.transmission && <span style={badge('var(--bg3)', 'var(--text2)')}>{item.transmission}</span>}
                  {item.color && <span style={badge('var(--bg3)', 'var(--text2)')}>{item.color}</span>}
                  {item.grade && <span style={badge('var(--bg3)', 'var(--text2)')}>Grade: {item.grade}</span>}
                </div>

                {item.start_price && (
                  <div style={{ marginTop: 10, color: 'var(--text1)', fontWeight: 700, fontSize: 16 }}>
                    {fmtJPY(item.start_price)}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  // ===================== DETAIL VIEW =====================
  if (view === 'detail' && detail) {
    const rb = resultBadge(detail.result);
    const images = detail.images || [];

    const infoFields = [
      { label: 'Auction', value: detail.auction },
      { label: 'Date', value: detail.date },
      { label: 'Lot ID', value: detail.lot_id },
      { label: 'Chassis Number', value: detail.chassis_number },
      { label: 'Frame', value: detail.frame },
      { label: 'Maker', value: detail.maker },
      { label: 'Model', value: detail.model },
      { label: 'Year', value: detail.year },
      { label: 'Grade', value: detail.grade },
      { label: 'Score', value: detail.score },
      { label: 'Mileage', value: detail.mileage ? `${Number(detail.mileage).toLocaleString()} km` : null },
      { label: 'Displacement', value: detail.displacement ? `${detail.displacement} cc` : null },
      { label: 'Transmission', value: detail.transmission },
      { label: 'AWD', value: detail.awd },
      { label: 'Color', value: detail.color },
      { label: 'Inspection', value: detail.inspection },
      { label: 'Options', value: detail.options },
    ];

    return (
      <div style={{ padding: '16px', maxWidth: 900, margin: '0 auto' }}>
        {/* Back button */}
        <button
          onClick={() => { setView('results'); setDetail(null); }}
          style={{ background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 8, padding: '6px 12px', color: 'var(--text2)', fontSize: 13, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4, marginBottom: 16 }}
        >
          <ChevronLeft size={16} /> Back to Results
        </button>

        {/* Price cards */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 20 }}>
          <div style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 12, padding: 20, textAlign: 'center' }}>
            <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 4, textTransform: 'uppercase', letterSpacing: 1 }}>Start Price</div>
            <div style={{ fontSize: 28, fontWeight: 800, color: 'var(--text1)' }}>{fmtJPY(detail.start_price)}</div>
          </div>
          <div style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 12, padding: 20, textAlign: 'center' }}>
            <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 4, textTransform: 'uppercase', letterSpacing: 1 }}>End Price</div>
            <div style={{ fontSize: 28, fontWeight: 800, color: detail.end_price ? 'var(--green)' : 'var(--text3)' }}>{fmtJPY(detail.end_price)}</div>
          </div>
        </div>

        {/* Result badge */}
        {detail.result && rb && (
          <div style={{ marginBottom: 16 }}>
            <span style={{ ...badge(rb.bg, rb.color), fontSize: 13, padding: '4px 12px' }}>{detail.result}</span>
          </div>
        )}

        {/* Image gallery */}
        {images.length > 0 && (
          <div style={{ marginBottom: 20 }}>
            <div style={{ fontSize: 13, color: 'var(--text3)', marginBottom: 8 }}>{images.length} photo{images.length !== 1 ? 's' : ''}</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 8 }}>
              {images.map((img, idx) => (
                <img
                  key={idx}
                  src={img}
                  alt={`Lot photo ${idx + 1}`}
                  style={{ width: '100%', height: 100, objectFit: 'cover', borderRadius: 8, cursor: 'pointer', background: 'var(--bg3)', border: '1px solid var(--border)' }}
                  loading="lazy"
                  onClick={() => setLightbox({ images, index: idx })}
                />
              ))}
            </div>
          </div>
        )}

        {/* Info grid */}
        <div style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 12, padding: 16 }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 12 }}>
            {infoFields.map(({ label, value }) => value != null && value !== '' && (
              <div key={label}>
                <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 2, textTransform: 'uppercase', letterSpacing: 0.5 }}>{label}</div>
                <div style={{ fontSize: 14, color: 'var(--text1)', fontWeight: 500 }}>{value}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Lightbox */}
        {lightbox && (
          <div
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.9)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            onClick={() => setLightbox(null)}
          >
            <button
              onClick={(e) => { e.stopPropagation(); setLightbox(null); }}
              style={{ position: 'absolute', top: 16, right: 16, background: 'rgba(255,255,255,0.1)', border: 'none', borderRadius: '50%', width: 40, height: 40, color: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            >
              <X size={20} />
            </button>

            {lightbox.images.length > 1 && (
              <button
                onClick={(e) => { e.stopPropagation(); setLightbox(prev => ({ ...prev, index: (prev.index - 1 + prev.images.length) % prev.images.length })); }}
                style={{ position: 'absolute', left: 16, background: 'rgba(255,255,255,0.1)', border: 'none', borderRadius: '50%', width: 44, height: 44, color: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
              >
                <ChevronLeft size={24} />
              </button>
            )}

            <img
              src={lightbox.images[lightbox.index]}
              alt=""
              style={{ maxWidth: '85vw', maxHeight: '85vh', objectFit: 'contain', borderRadius: 8 }}
              onClick={e => e.stopPropagation()}
            />

            {lightbox.images.length > 1 && (
              <button
                onClick={(e) => { e.stopPropagation(); setLightbox(prev => ({ ...prev, index: (prev.index + 1) % prev.images.length })); }}
                style={{ position: 'absolute', right: 16, background: 'rgba(255,255,255,0.1)', border: 'none', borderRadius: '50%', width: 44, height: 44, color: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
              >
                <ChevronRight size={24} />
              </button>
            )}

            <div style={{ position: 'absolute', bottom: 20, color: 'rgba(255,255,255,0.7)', fontSize: 13 }}>
              {lightbox.index + 1} / {lightbox.images.length}
            </div>
          </div>
        )}
      </div>
    );
  }

  return null;
}

export default MarketPrices;
